import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { sweetConfirm } from "../utils/sweetAlerts";

const API = "/backend/public";
const EMPTY_FORM = {
  academy_category: "",
  course_model_id: "",
  course_category_id: "",
  selection_type: "single",
  course_id: "",
  course_ids: [],
  state_id: "",
  fee_amount: "",
  registration_fee: "",
  exam_fee: "",
};

function CourseFeesManager() {
  const [meta, setMeta] = useState({ academy_categories: [], models: [], categories: [], courses: [], states: [] });
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({ search: "", course_id: "", state_id: "" });
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadMeta = async () => {
    const res = await axios.get(`${API}/api/course-setup/meta`);
    setMeta(res.data || { academy_categories: [], models: [], categories: [], courses: [], states: [] });
  };

  const loadRows = async () => {
    const res = await axios.get(`${API}/api/course-fees-admin`, { params: filters });
    setRows(res.data || []);
  };

  useEffect(() => {
    Promise.all([loadMeta(), loadRows()]).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredModules = useMemo(
    () => (meta.models || []).filter((item) => !form.academy_category || item.academy_category === form.academy_category),
    [meta.models, form.academy_category]
  );

  const filteredCategories = useMemo(
    () =>
      (meta.categories || []).filter(
        (item) =>
          (!form.academy_category || item.academy_category === form.academy_category) &&
          (!form.course_model_id || String(item.course_model_id) === String(form.course_model_id))
      ),
    [meta.categories, form.academy_category, form.course_model_id]
  );

  const filteredCourses = useMemo(
    () =>
      (meta.courses || []).filter(
        (item) =>
          (!form.academy_category || item.academy_category === form.academy_category) &&
          (!form.course_model_id || String(item.course_model_id) === String(form.course_model_id)) &&
          (!form.course_category_id || String(item.course_category_id) === String(form.course_category_id))
      ),
    [meta.courses, form.academy_category, form.course_model_id, form.course_category_id]
  );

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleHierarchyChange = (field, value) => {
    setForm((prev) => {
      if (field === "academy_category") {
        return {
          ...prev,
          academy_category: value,
          course_model_id: "",
          course_category_id: "",
          course_id: "",
          course_ids: [],
        };
      }
      if (field === "course_model_id") {
        return {
          ...prev,
          course_model_id: value,
          course_category_id: "",
          course_id: "",
          course_ids: [],
        };
      }
      if (field === "course_category_id") {
        return {
          ...prev,
          course_category_id: value,
          course_id: "",
          course_ids: [],
        };
      }
      if (field === "selection_type") {
        return {
          ...prev,
          selection_type: value,
          course_id: "",
          course_ids: [],
        };
      }
      return prev;
    });
  };

  const toggleMultipleCourse = (courseId) => {
    const key = String(courseId);
    setForm((prev) => {
      const exists = prev.course_ids.includes(key);
      return {
        ...prev,
        course_ids: exists ? prev.course_ids.filter((id) => id !== key) : [...prev.course_ids, key],
      };
    });
  };

  const selectAllVisibleCourses = () => {
    setForm((prev) => ({ ...prev, course_ids: filteredCourses.map((item) => String(item.id)) }));
  };

  const clearMultipleSelection = () => {
    setForm((prev) => ({ ...prev, course_ids: [] }));
  };

  const resolveTargetCourseIds = () => {
    if (form.selection_type === "single") {
      return form.course_id ? [Number(form.course_id)] : [];
    }
    if (form.selection_type === "multiple") {
      return form.course_ids.map((id) => Number(id)).filter(Boolean);
    }
    return filteredCourses.map((item) => Number(item.id)).filter(Boolean);
  };

  const saveFee = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const targetCourseIds = editingId ? [Number(form.course_id)].filter(Boolean) : resolveTargetCourseIds();

      if (!targetCourseIds.length) {
        alert("Please select at least one course.");
        setSaving(false);
        return;
      }

      const payload = {
        academy_category: form.academy_category || null,
        course_model_id: form.course_model_id || null,
        course_category_id: form.course_category_id || null,
        selection_type: form.selection_type,
        course_id: targetCourseIds[0] || null,
        course_ids: targetCourseIds,
        state_id: Number(form.state_id || 0),
        fee_amount: Number(form.fee_amount || 0),
        registration_fee: Number(form.registration_fee || 0),
        exam_fee: Number(form.exam_fee || 0),
      };

      const res = await axios.post(editingId ? `${API}/api/course-fees-admin/${editingId}` : `${API}/api/course-fees-admin`, payload);
      resetForm();
      await loadRows();
      alert(
        res?.data?.message ||
          (editingId
            ? "Course fee updated successfully."
            : "Course fee saved successfully.")
      );
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save course fee.");
    } finally {
      setSaving(false);
    }
  };

  const deleteFee = async (id) => {
    if (!(await sweetConfirm("Are you sure you want to delete this course fee?", { confirmButtonText: "Yes, Delete" }))) return;
    try {
      await axios.delete(`${API}/api/course-fees-admin/${id}`);
      if (editingId === id) {
        resetForm();
      }
      await loadRows();
      alert("Course fee deleted successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete course fee.");
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <h3 className="mb-1">Course Fees</h3>
            <form onSubmit={saveFee}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Academy Category</label>
                  <select
                    className="form-select"
                    value={form.academy_category}
                    onChange={(e) => handleHierarchyChange("academy_category", e.target.value)}
                    required
                  >
                    <option value="">Select Academy Category</option>
                    {(meta.academy_categories || []).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Course Module</label>
                  <select
                    className="form-select"
                    value={form.course_model_id}
                    onChange={(e) => handleHierarchyChange("course_model_id", e.target.value)}
                    required
                  >
                    <option value="">Select Module</option>
                    {filteredModules.map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Course Category</label>
                  <select
                    className="form-select"
                    value={form.course_category_id}
                    onChange={(e) => handleHierarchyChange("course_category_id", e.target.value)}
                    required
                  >
                    <option value="">Select Category</option>
                    {filteredCategories.map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Course Selection Mode</label>
                  <select
                    className="form-select"
                    value={form.selection_type}
                    onChange={(e) => handleHierarchyChange("selection_type", e.target.value)}
                    disabled={Boolean(editingId)}
                    required
                  >
                    <option value="single">Single Course</option>
                    <option value="multiple">Multiple Course</option>
                    <option value="all">Select All Course</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">State</label>
                  <select
                    className="form-select"
                    value={form.state_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, state_id: e.target.value }))}
                    required
                  >
                    <option value="">Select State</option>
                    {(meta.states || []).map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Course Fee</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.fee_amount}
                    onChange={(e) => setForm((prev) => ({ ...prev, fee_amount: e.target.value }))}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                {(form.selection_type === "single" || editingId) ? (
                  <div className="col-md-12">
                    <label className="form-label">Course</label>
                    <select
                      className="form-select"
                      value={form.course_id}
                      onChange={(e) => setForm((prev) => ({ ...prev, course_id: e.target.value }))}
                      required
                    >
                      <option value="">Select Course</option>
                      {filteredCourses.map((item) => (
                        <option key={item.id} value={item.id}>{item.title}</option>
                      ))}
                    </select>
                  </div>
                ) : null}

                {form.selection_type === "multiple" && !editingId ? (
                  <div className="col-md-12">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                      <label className="form-label mb-0">Select Multiple Courses</label>
                      <div className="d-flex gap-2">
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectAllVisibleCourses}>
                          Select All Visible
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearMultipleSelection}>
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="border rounded-3 p-2" style={{ maxHeight: "220px", overflowY: "auto", background: "#f8fafc" }}>
                      {!filteredCourses.length ? (
                        <div className="text-muted small p-2">No courses found under selected filters.</div>
                      ) : (
                        filteredCourses.map((item) => {
                          const checked = form.course_ids.includes(String(item.id));
                          return (
                            <label key={item.id} className="d-flex align-items-center gap-2 px-2 py-1 rounded-2" style={{ cursor: "pointer" }}>
                              <input type="checkbox" checked={checked} onChange={() => toggleMultipleCourse(item.id)} />
                              <span>{item.title}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    <div className="small text-muted mt-1">Selected: {form.course_ids.length}</div>
                  </div>
                ) : null}

                {form.selection_type === "all" && !editingId ? (
                  <div className="col-md-12">
                    <div className="alert alert-info py-2 mb-0">
                      All courses under selected category will be updated. Total courses: <strong>{filteredCourses.length}</strong>
                    </div>
                  </div>
                ) : null}

                <div className="col-md-6">
                  <label className="form-label">Registration Fee</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.registration_fee}
                    onChange={(e) => setForm((prev) => ({ ...prev, registration_fee: e.target.value }))}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Exam Fee</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.exam_fee}
                    onChange={(e) => setForm((prev) => ({ ...prev, exam_fee: e.target.value }))}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="mt-3">
                <button className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : editingId ? "Update Fee" : "Save Fee"}</button>
                {editingId ? (
                  <button type="button" className="btn btn-outline-secondary ms-2" onClick={resetForm}>
                    Cancel Edit
                  </button>
                ) : null}
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <input className="form-control" placeholder="Search course or state" value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <select className="form-select" value={filters.course_id} onChange={(e) => setFilters((prev) => ({ ...prev, course_id: e.target.value }))}>
                  <option value="">All Courses</option>
                  {(meta.courses || []).map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <select className="form-select" value={filters.state_id} onChange={(e) => setFilters((prev) => ({ ...prev, state_id: e.target.value }))}>
                  <option value="">All States</option>
                  {(meta.states || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <button className="btn btn-outline-dark w-100" onClick={loadRows}>Apply Filter</button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Course</th>
                    <th>State</th>
                    <th>Total Fee</th>
                    <th>Registration Fee</th>
                    <th>Exam Fee</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div>{item.course_title || "-"}</div>
                        <div className="small text-muted">{item.model_name || "-"} / {item.category_name || "-"}</div>
                      </td>
                      <td>{item.state_name || "-"}</td>
                      <td>Rs {Number(item.fee_amount || 0).toFixed(2)}</td>
                      <td>Rs {Number(item.registration_fee || 0).toFixed(2)}</td>
                      <td>Rs {Number(item.exam_fee || 0).toFixed(2)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              const selectedCourse = (meta.courses || []).find((course) => String(course.id) === String(item.course_id));
                              setEditingId(item.id);
                              setForm({
                                academy_category: selectedCourse?.academy_category ? String(selectedCourse.academy_category) : "",
                                course_model_id: selectedCourse?.course_model_id ? String(selectedCourse.course_model_id) : "",
                                course_category_id: selectedCourse?.course_category_id ? String(selectedCourse.course_category_id) : "",
                                selection_type: "single",
                                course_id: String(item.course_id || ""),
                                course_ids: [],
                                state_id: String(item.state_id || ""),
                                fee_amount: item.fee_amount || "",
                                registration_fee: item.registration_fee || "",
                                exam_fee: item.exam_fee || "",
                              });
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteFee(item.id)} title="Delete">
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan="7" className="text-center text-muted py-4">No course fee found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default CourseFeesManager;
